import { Alert } from 'react-bootstrap';
import React, { Fragment, useState } from 'react';
import { Form } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import AutocompletedAddressFormGroup from 'components/form/autocompleted_address_form_group';
import BrokersList from './brokers_list';
import ErrorAlert from './error_alert';
import { countryListPropType } from './prop_types';
import Button from 'shared/button';

export default function CreatePropertyForm({
  addressNotFound,
  brokers,
  countryList,
  errorMessages,
  formFields,
  gotoPrevStep,
  hasErrors,
  hasInsights,
  listingNotFound,
  maxBrokersLimit,
  newListing,
  saving,
  showBrokerSelect,
  onBrokerChange,
  onChange,
  onFormSubmit,
}) {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const { address, city, country, state, zip } = formFields;

  const isStateRequired = country.countryStates.length !== 0;
  const isZipRequired = country.zipLabel !== '';

  const isAddressInvalid = !address;
  const isCityInvalid = !city;
  const isCountryInvalid = !country?.hasOwnProperty('id');
  const isStateInvalid = isStateRequired && !state;
  const isZipInvalid = isZipRequired && !zip;
  const isBrokersInvalid = showBrokerSelect && brokers.some(item => isEmpty(item));

  const handleFormSubmit = (e) => {
    e.preventDefault();

    setFormSubmitted(true);

    const isFormInvalid = isAddressInvalid ||
      isCityInvalid ||
      isCountryInvalid ||
      isStateInvalid ||
      isZipInvalid ||
      isBrokersInvalid;

    if (!isFormInvalid) onFormSubmit();
  };

  const buttonText = () => {
    if (listingNotFound) {
      return saving ? 'Creating Listing...' : 'Create Listing';
    } else if (newListing) {
      return saving ? 'Searching...' : 'Search';
    }

    return '';
  };

  return (
    <Fragment>
      <h3 className="mb-5 mt-0 text-center" style={{ fontWeight: 'bold' }}>Create a new listing</h3>

      {newListing && (
        <Fragment>
          <div className="mb-5 spaced-text text-center">
            Search for a property and Buildout Insights will auto-populate the property information we have,
            or you can create a brand new listing.
          </div>

          <h3 className="mb-4">Search for Property</h3>
        </Fragment>
      )}
      <ErrorAlert errorMessages={errorMessages} hasErrors={hasErrors}/>
      {listingNotFound && hasInsights && !hasErrors && (
        <Fragment>
          <Alert bsStyle="info" className="d-flex mb-5">
            <h4 className="m-0 alert-header">
              The address {addressNotFound} was not found in Insights. Creating new property.
            </h4>
          </Alert>

          <h3 className="mb-4">Create a new listing</h3>
        </Fragment>
      )}

      <Form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
        {countryList.length > 1 && (
          <Form.Group className="mb-4">
            <Form.Label>Country<span>*</span></Form.Label>
            <Form.Select
              autoFocus={countryList.length > 1}
              className="form-control"
              isInvalid={isCountryInvalid && formSubmitted}
              name="country"
              required
              value={country.id}
              onChange={(e) => {
                onChange({ country: countryList.find(country => country.id === Number(e.target.value)) });
              }}
            >
              {countryList.map(country => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Form.Select>
            {isCountryInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
              This field is required
            </Form.Control.Feedback>}
          </Form.Group>
        )}

        <AutocompletedAddressFormGroup
          {...formFields}
          feedback={isAddressInvalid && formSubmitted && 'This field is required'}
          feedbackType="invalid"
          hasError={isAddressInvalid && formSubmitted}
          required
          onChange={onChange}
        />

        <Form.Group className="mb-4">
          <Form.Label>City<span>*</span></Form.Label>
          <Form.Control
            isInvalid={isCityInvalid && formSubmitted}
            name="city"
            required
            type="text"
            value={city}
            onChange={e => onChange({ city: e.target.value })}
          />
          {isCityInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        <Form.Group className="mb-4 row">
          {(country.countryStates.length !== 0) && (
            <div className="col-md-8">
              <Form.Label>{country.stateLabel}<span>*</span></Form.Label>
              <Form.Select
                className="form-control"
                isInvalid={isStateInvalid && formSubmitted}
                name="state"
                required
                value={state}
                onChange={e => onChange({ state: e.target.value })}
              >
                <option value="">Select a state</option>
                {country.countryStates.map(state => (
                  <option key={state.code} value={state.code}>{state.displayName}</option>
                ))}
              </Form.Select>
              {isStateInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
                This field is required
              </Form.Control.Feedback>}
            </div>
          )}

          {country.zipLabel && (
            <div className="col-md-4">
              <Form.Label>{country.zipLabel}<span>*</span></Form.Label>
              <Form.Control
                color="red"
                isInvalid={isZipInvalid && formSubmitted}
                name="zip"
                required
                type="string"
                value={zip}
                onChange={e => onChange({ zip: e.target.value })}
              />
              {isZipInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
                This field is required
              </Form.Control.Feedback>}
            </div>
          )}
        </Form.Group>

        {showBrokerSelect && <Form.Group className="mb-4">
          <BrokersList
            brokers={brokers}
            isInvalid={isBrokersInvalid && formSubmitted}
            maxBrokersLimit={maxBrokersLimit}
            required={true}
            onSetBrokers={brokers => onBrokerChange(brokers)}
          />
          {isBrokersInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>}

        <div className="mt-5">
          {gotoPrevStep && (
            <Button.Accent className="mr-3" outlined type="button" onClick={gotoPrevStep}>Back</Button.Accent>
          )}
          <Button.Primary disabled={saving} name="submit-listing" type="submit">{buttonText()}</Button.Primary>
        </div>
      </Form>
    </Fragment>
  );
}

CreatePropertyForm.propTypes = {
  addressNotFound: PropTypes.string.isRequired,
  brokers: PropTypes.array.isRequired,
  countryList: countryListPropType.isRequired,
  errorMessages: PropTypes.object,
  formFields: PropTypes.object.isRequired,
  gotoPrevStep: PropTypes.func,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  listingNotFound: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  newListing: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  onBrokerChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
};
